// paletteStore — module singleton that owns the active UI palette.
//
// NOT React state (CLAUDE.md hard rule): switching a palette writes CSS custom
// properties onto <html>, and the browser repaints. The graph hot path
// (BlueprintEditor / EventGraph / GraphEvaluator) never re-renders. UI surfaces
// that need to reflect the *selection* (the Appearance panel, the menu check)
// subscribe for a change tick, exactly like textureQuality.js.
//
// Persistence is write-through and per-user:
//   • scoped localStorage key (scopeKey('aos-palette')) — authoritative local
//     cache, namespaced by user id so two users on one browser don't collide.
//   • an unscoped boot-hint key — applied synchronously at import so a
//     non-default user doesn't flash the default theme before auth resolves.
//   • the server (userPreferencesApi) — cross-machine source of truth when
//     reachable; reconciled in initPalette() after auth.

import { scopeKey } from '../userScope'
import { DEFAULT_PALETTE_ID, getPalette } from './registry'
import { paletteToVars } from './tokens'
import { fetchRemotePalette, saveRemotePalette } from './userPreferencesApi'

export const PALETTE_KEY_BASE = 'aos-palette'
// Unscoped boot hint — last palette applied on this browser, any user. Used
// only to avoid a first-paint flash; the scoped value + server reconcile it.
export const PALETTE_HINT_KEY = 'aos-palette-last'

const _state = { id: DEFAULT_PALETTE_ID, initialized: false }
const _listeners = new Set()

function _storage() {
  try { return window.localStorage } catch { return null }
}

function _notify() {
  for (const fn of _listeners) {
    try { fn() } catch (e) { console.warn('[palette] listener failed:', e) }
  }
}

export function subscribe(fn) {
  _listeners.add(fn)
  return () => _listeners.delete(fn)
}

// Write a palette's tokens onto the document root and tag it for CSS hooks
// (e.g. [data-theme="light"] overrides, scrollbar theming).
function applyVars(palette) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const vars = paletteToVars(palette)
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v)
  root.setAttribute('data-palette', palette.id)
  root.setAttribute('data-theme', palette.isLight ? 'light' : 'dark')
}

export function getPaletteId() { return _state.id }
export function getCurrentPalette() { return getPalette(_state.id) }

// Set the active palette: repaint + notify + persist (local + hint + server).
// `persist:false` applies without writing (used by the reconcile path so a
// server read doesn't echo straight back to the server).
export function setPalette(id, { persist = true } = {}) {
  const palette = getPalette(id)          // falls back to default on unknown id
  _state.id = palette.id
  applyVars(palette)
  _notify()
  if (persist) {
    const s = _storage()
    try { s?.setItem(scopeKey(PALETTE_KEY_BASE), palette.id) } catch { /* best-effort */ }
    try { s?.setItem(PALETTE_HINT_KEY, palette.id) } catch { /* best-effort */ }
    void saveRemotePalette(palette.id)
  }
  return palette.id
}

// applyBootHint — synchronous, pre-auth. Applies the unscoped last-used palette
// so a returning non-default user doesn't flash the default theme. Safe to call
// at import; does nothing if no hint (tokens.css :root already provides the
// default values, so vars always resolve).
export function applyBootHint() {
  const hint = (() => { try { return _storage()?.getItem(PALETTE_HINT_KEY) } catch { return null } })()
  if (hint) applyVars(getPalette(hint))
}

// initPalette — post-auth reconcile. scopeKey() now resolves to the real user
// id. Reads the scoped local value (authoritative cache), then the server
// (cross-machine truth); if the server has a different value it wins and is
// mirrored locally. No stored value anywhere -> the default palette (a missing
// preference is normal, never an error). Idempotent.
export async function initPalette() {
  if (_state.initialized) return _state.id
  _state.initialized = true

  const local = (() => { try { return _storage()?.getItem(scopeKey(PALETTE_KEY_BASE)) } catch { return null } })()
  setPalette(local || DEFAULT_PALETTE_ID, { persist: false })

  const remote = await fetchRemotePalette()
  if (remote && remote !== _state.id) {
    // Server wins; mirror to the local cache + hint but don't echo back.
    setPalette(remote, { persist: false })
    const s = _storage()
    try { s?.setItem(scopeKey(PALETTE_KEY_BASE), remote) } catch { /* best-effort */ }
    try { s?.setItem(PALETTE_HINT_KEY, remote) } catch { /* best-effort */ }
  } else if (local && local !== DEFAULT_PALETTE_ID) {
    // Local had a non-default choice the server didn't know about (first sync,
    // or server unreachable last time) — push it up. Best-effort.
    void saveRemotePalette(local)
  }
  return _state.id
}

// Expose a small console/e2e handle. HMR-safe (window survives module reload).
if (typeof window !== 'undefined') {
  window.__PALETTE__ = {
    get id() { return _state.id },
    set: (id) => setPalette(id),
    get: () => getCurrentPalette(),
  }
}
